import React, { useCallback, useContext } from "react"

import styled, { css } from "styled-components"

import { borderRadius, colors, spacing, typography } from "@izettle/otto"

import { RadioGroupContext } from "./context"

enum KeyCode {
  ENTER = 13,
  SPACE = 32,
}

export type RadioProps<T = any> = {
  value: T
  title: React.ReactNode
  description?: React.ReactNode
  disabled?: boolean
}

const RadioContainer = styled.label<{ checked: boolean; disabled?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  margin: ${spacing.xSmall} 0;
  border-radius: ${borderRadius.small};
  padding: ${spacing.small};

  ${({ checked }) =>
    checked
      ? css`
          border: 1px solid ${colors.action.secondary.border.hover};
          background: ${colors.action.secondary.background.hover};
        `
      : css`
          border: 1px solid ${colors.action.secondary.border.default};
          background: ${colors.background.default};
        `}

  ${({ disabled }) =>
    disabled &&
    css`
      cursor: not-allowed;
      border: 1px solid ${colors.action.secondary.border.disabled};
      background: ${colors.action.secondary.background.disabled};
    `}
`

const RadioIcon = styled.i<{ checked: boolean }>`
  position: relative;
  display: block;
  font-style: normal;
  background: transparent;
  margin: 0;
  width: 14px;
  height: 14px;
  margin-right: ${spacing.small};

  :after,
  :before {
    box-sizing: border-box;
    position: absolute;
    content: " ";
  }

  /* Outer circle */
  :before {
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    border-radius: 50%;
    background: none;
    border: 1px solid;
    border-color: ${({ checked }) =>
      checked ? colors.action.primary.border.selected : colors.icon.secondary};
  }

  /* Inner dot */
  :after {
    top: 50%;
    left: 50%;
    transform: translate(-50%, -50%);
    width: 8px;
    height: 8px;
    border-radius: 50%;
    opacity: ${({ checked }) => (checked ? "1" : "0")};
    transition: opacity 50ms ease-in-out;
    background: ${colors.action.primary.background.selected};
  }
`

const HiddenInput = styled.input`
  display: none;
`

const Content = styled.div`
  flex: 1;
`

const Title = styled.div<{ checked: boolean; disabled?: boolean }>`
  ${typography.regular}
  color: ${({ checked }) =>
    checked ? colors.action.secondary.text.default : colors.text.default};

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${colors.action.secondary.text.disabled};
    `}
`

const Description = styled.div<{ checked: boolean; disabled?: boolean }>`
  ${typography.mini}
  color: ${({ checked }) =>
    checked ? colors.action.secondary.text.default : colors.text.secondary};

  ${({ disabled }) =>
    disabled &&
    css`
      color: ${colors.action.secondary.text.disabled};
    `}
`

export const Radio = ({ value, title, description, disabled }: RadioProps) => {
  const { value: selectedValue, name, onChange } = useContext(RadioGroupContext)
  const onRadioLabelKeyDown = useCallback((e: React.KeyboardEvent<HTMLLabelElement>) => {
    if ([KeyCode.ENTER, KeyCode.SPACE].includes(e.keyCode)) {
      e.currentTarget.click()
    }
  }, [])

  const isChecked = selectedValue === value

  return (
    <RadioContainer
      checked={isChecked}
      tabIndex={0}
      onKeyDown={onRadioLabelKeyDown}
      disabled={disabled}
    >
      <HiddenInput
        disabled={disabled}
        name={name}
        type="radio"
        value={value}
        checked={isChecked}
        onChange={onChange}
      />
      <RadioIcon checked={isChecked} />
      <Content>
        <Title disabled={disabled} checked={isChecked}>
          {title}
        </Title>
        {description && (
          <Description disabled={disabled} checked={isChecked}>
            {description}
          </Description>
        )}
      </Content>
    </RadioContainer>
  )
}
